import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { rate3, rate2, cnt, EMDASH } from "./statFormat";
import { Flame, ArrowRight, Activity } from "lucide-react";

// Consecutive games (newest-first) with a hit.
function hitStreak(items) {
  let n = 0;
  for (const g of items) {
    if (cnt(g.hitting?.h) > 0) n += 1;
    else if (cnt(g.hitting?.ab) > 0 || cnt(g.hitting?.pa) > 0) break; // played & hitless -> streak ends
  }
  return n;
}

export default function RecentPerformance({ playerId, hasData, onGoTo }) {
  const [agg, setAgg] = useState(null);
  const [log, setLog] = useState([]);

  useEffect(() => {
    if (!hasData) return;
    api.get(`/players/${playerId}/game-stats`, { params: { scope: "last5" } }).then((r) => setAgg(r.data?.aggregate)).catch(() => {});
    api.get(`/players/${playerId}/game-log`, { params: { limit: 5 } }).then((r) => setLog(r.data?.items || [])).catch(() => {});
  }, [playerId, hasData]);

  if (!hasData || !agg || agg.games === 0) return null;

  const h = agg.hitting?.rates || {};
  const hasHit = agg.games_hit > 0;
  const hasPitch = agg.games_pitched > 0;
  const pit = agg.pitching || {};
  const streak = hitStreak(log);
  const recentTourn = log.find((g) => g.tournament_name)?.tournament_name;

  return (
    <div data-testid="recent-performance" className="border border-slate-200 rounded-sm overflow-hidden">
      <div className="bg-[#0A192F] text-white px-5 py-3 flex items-center gap-2">
        <Activity className="w-4 h-4 text-[#E63946]" />
        <span className="font-display uppercase tracking-tight text-sm">Recent Performance</span>
        <span className="text-[10px] uppercase tracking-wider text-white/50 ml-auto">Last {agg.games} game{agg.games === 1 ? "" : "s"}</span>
      </div>
      <div className="p-5">
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          {hasHit && (
            <>
              <Stat label="AVG" value={rate3(h.avg)} />
              <Stat label="OPS" value={rate3(h.ops)} />
              <Stat label="HR" value={cnt(agg.hitting.totals.hr)} />
              <Stat label="RBI" value={cnt(agg.hitting.totals.rbi)} />
            </>
          )}
          {hasPitch && (
            <>
              <Stat label="ERA" value={rate2(pit.rates?.era)} />
              <Stat label="IP" value={pit.totals?.ip || EMDASH} />
              <Stat label="SO" value={cnt(pit.totals?.so)} />
              <Stat label="WHIP" value={rate2(pit.rates?.whip)} />
            </>
          )}
        </div>

        <div className="flex flex-wrap items-center gap-3 mt-4">
          {streak >= 2 && (
            <span data-testid="recent-streak" className="inline-flex items-center gap-1.5 bg-amber-100 text-amber-800 text-xs font-semibold px-2.5 py-1 rounded-sm">
              <Flame className="w-3.5 h-3.5" /> {streak}-game hit streak
            </span>
          )}
          {recentTourn && <span className="text-xs text-slate-500">Latest: {recentTourn}</span>}
        </div>

        <div className="flex flex-wrap gap-2 mt-4">
          <button data-testid="recent-goto-stats" onClick={() => onGoTo("statistics")} className="inline-flex items-center gap-1 text-xs font-semibold uppercase tracking-wider text-[#E63946] hover:underline">
            Full Statistics <ArrowRight className="w-3.5 h-3.5" />
          </button>
          <button data-testid="recent-goto-log" onClick={() => onGoTo("gamelog")} className="inline-flex items-center gap-1 text-xs font-semibold uppercase tracking-wider text-[#0A192F] hover:underline">
            Game Log <ArrowRight className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>
    </div>
  );
}

const Stat = ({ label, value }) => (
  <div>
    <div className="font-data text-2xl text-[#0A192F] tabular-nums">{value}</div>
    <div className="text-[10px] uppercase tracking-wider text-slate-400">{label}</div>
  </div>
);
